import React, { useState, useEffect } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';
import { formatDate } from '../../utils/formatters';

interface NotificationPreference {
  id?: number;
  channel: 'email' | 'in_app' | 'webhook';
  enabled: boolean;
  events: string[];
  settings?: {
    webhook_url?: string;
    email?: string;
    format?: string;
  };
}

interface Notification {
  id: number;
  type: string;
  event: string;
  status: string;
  subject: string;
  body: string;
  created_at: string;
  sent_at?: string;
  failed_at?: string;
}

interface NotificationStats {
  stats: {
    pending: number;
    sent: number;
    failed: number;
    total: number;
  };
  recent: Notification[];
}

const AVAILABLE_EVENTS = [
  { value: 'rule_created', label: 'Rule Created' },
  { value: 'rule_updated', label: 'Rule Updated' },
  { value: 'rule_deleted', label: 'Rule Deleted' },
  { value: 'report_updated', label: 'Report Updated' },
  { value: 'bulk_operation_completed', label: 'Bulk Operation Completed' },
  { value: 'bulk_operation_failed', label: 'Bulk Operation Failed' },
];

export const NotificationCenter: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [testChannel, setTestChannel] = useState<'email' | 'in_app' | 'webhook'>('email');
  const [testEvent, setTestEvent] = useState('rule_created');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchPreferences();
    fetchStats();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v2/notifications/preferences`, {
        headers: getAuthHeaders() as HeadersInit,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch preferences');
      }

      const data = await response.json();
      const prefs = data.preferences || [];
      
      // Ensure we have all channels
      const channels: Array<'email' | 'in_app' | 'webhook'> = ['email', 'in_app', 'webhook'];
      const existingChannels = prefs.map((p: NotificationPreference) => p.channel);
      
      channels.forEach(channel => {
        if (!existingChannels.includes(channel)) {
          prefs.push({
            channel,
            enabled: false,
            events: [],
            settings: {},
          });
        }
      });

      setPreferences(prefs);
    } catch (error) {
      console.error('Error fetching preferences:', error);
      setMessage({ type: 'error', text: 'Failed to load notification preferences' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v2/notifications/stats`, {
        headers: getAuthHeaders() as HeadersInit,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handlePreferenceChange = (channel: string, field: string, value: any) => {
    setPreferences(prev => prev.map(pref => 
      pref.channel === channel 
        ? { ...pref, [field]: value }
        : pref
    ));
  };

  const handleEventToggle = (channel: string, event: string) => {
    setPreferences(prev => prev.map(pref => {
      if (pref.channel === channel) {
        const events = pref.events.includes(event)
          ? pref.events.filter(e => e !== event)
          : [...pref.events, event];
        return { ...pref, events };
      }
      return pref;
    }));
  };

  const handleSettingChange = (channel: string, setting: string, value: string) => {
    setPreferences(prev => prev.map(pref => {
      if (pref.channel === channel) {
        return {
          ...pref,
          settings: {
            ...pref.settings,
            [setting]: value,
          },
        };
      }
      return pref;
    }));
  };

  const savePreferences = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_BASE_URL}/v2/notifications/preferences`, {
        method: 'PUT',
        headers: getAuthHeaders() as HeadersInit,
        body: JSON.stringify({ preferences }),
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      setMessage({ type: 'success', text: 'Notification preferences saved successfully' });
      await fetchPreferences();
    } catch (error) {
      console.error('Error saving preferences:', error);
      setMessage({ type: 'error', text: 'Failed to save notification preferences' });
    } finally {
      setSaving(false);
    }
  };

  const testNotification = async () => {
    setTesting(true);
    setMessage(null);

    try {
      const body: any = {
        channel: testChannel,
        event: testEvent,
      };

      if (testChannel === 'webhook' && webhookUrl) {
        body.webhook_url = webhookUrl;
      }

      const response = await fetch(`${API_BASE_URL}/v2/notifications/test`, {
        method: 'POST',
        headers: getAuthHeaders() as HeadersInit,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to send test notification');
      }

      setMessage({ type: 'success', text: `Test ${testChannel} notification sent successfully` });
      await fetchStats();
    } catch (error) {
      console.error('Error testing notification:', error);
      setMessage({ type: 'error', text: 'Failed to send test notification' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Notification Center</h2>
        <p className="mt-2 text-gray-600">
          Configure how you receive notifications about rule changes and system events
        </p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Statistics */}
      {stats && (
        <div className="mb-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">Total Notifications</div>
            <div className="text-2xl font-bold">{stats.stats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">Pending</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.stats.pending}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">Sent</div>
            <div className="text-2xl font-bold text-green-600">{stats.stats.sent}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">Failed</div>
            <div className="text-2xl font-bold text-red-600">{stats.stats.failed}</div>
          </div>
        </div>
      )}

      {/* Notification Preferences */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">Notification Preferences</h3>
        <div className="space-y-6">
          {preferences.map(pref => (
            <div key={pref.channel} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium capitalize">
                  {pref.channel.replace('_', ' ')} Notifications
                </h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pref.enabled}
                    onChange={(e) => handlePreferenceChange(pref.channel, 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {pref.enabled && (
                <>
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Events to notify</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {AVAILABLE_EVENTS.map(event => (
                        <label key={event.value} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={pref.events.includes(event.value)}
                            onChange={() => handleEventToggle(pref.channel, event.value)}
                            className="mr-2"
                          />
                          <span className="text-sm">{event.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {pref.channel === 'webhook' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Webhook URL
                      </label>
                      <input
                        type="url"
                        value={pref.settings?.webhook_url || ''}
                        onChange={(e) => handleSettingChange(pref.channel, 'webhook_url', e.target.value)}
                        placeholder="https://example.com/webhook"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}

                  {pref.channel === 'email' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address (optional, uses account email by default)
                      </label>
                      <input
                        type="email"
                        value={pref.settings?.email || ''}
                        onChange={(e) => handleSettingChange(pref.channel, 'email', e.target.value)}
                        placeholder="your@email.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6">
          <button
            onClick={savePreferences}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </div>

      {/* Test Notifications */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">Test Notifications</h3>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Channel
              </label>
              <select
                value={testChannel}
                onChange={(e) => setTestChannel(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="email">Email</option>
                <option value="in_app">In-App</option>
                <option value="webhook">Webhook</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event
              </label>
              <select
                value={testEvent}
                onChange={(e) => setTestEvent(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {AVAILABLE_EVENTS.map(event => (
                  <option key={event.value} value={event.value}>
                    {event.label}
                  </option>
                ))}
              </select>
            </div>

            {testChannel === 'webhook' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook URL
                </label>
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://example.com/webhook"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>

          <button
            onClick={testNotification}
            disabled={testing}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
          >
            {testing ? 'Sending...' : 'Send Test Notification'}
          </button>
        </div>
      </div>

      {/* Recent Notifications */}
      {stats && stats.recent.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Recent Notifications</h3>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.recent.map(notification => (
                  <tr key={notification.id}>
                    <td className="px-6 py-4 text-sm">{notification.type}</td>
                    <td className="px-6 py-4 text-sm">{notification.event.replace(/_/g, ' ')}</td>
                    <td className="px-6 py-4 text-sm">{notification.subject}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        notification.status === 'sent' 
                          ? 'bg-green-100 text-green-800'
                          : notification.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {notification.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(notification.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};